import type { Match } from '@uefadigital/panenka';
import { PkDivider, PkMatchHeader } from '@uefadigital/panenka-react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { LineUpsTab } from '../LineUpsTab';
import { MatchOfficialsTab } from '../MatchOfficialsTab';
import { SquadDetailsTab } from '../SquadDetailsTab';

import { useGetMatchDetailsSectionsQuery } from 'api/cmsApi';
import { FormGuide } from 'components/FormGuide';
import { HeadToHead } from 'components/HeadToHead';
import { StatisticsTab } from 'components/Match/StatisticsTab';
import { MatchHighlights } from 'components/MatchHighlights';
import { Sponsors } from 'components/Sponsors';
import { useMatchUnitTranslations } from 'hooks/useMatchUnitTranslations';

import * as style from './style.module.css';

interface PrintMatchPdfTabData {
  matchData: Match;
}

const fieldSize = {
  desktop: 100,
  mobile: 93,
  desktopJerseySize: 26,
  mobileJerseySize: 26,
};

const prevLineUpsFieldsSize = {
  desktop: 100,
  mobile: 100,
  desktopJerseySize: 22,
  mobileJerseySize: 22,
};

export const PrintMatchPdfTab = ({ matchData }: PrintMatchPdfTabData) => {
  const { t } = useTranslation();
  const Title = 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sm pk-font-style--normal pr-font-size--16';

  const muTranslations = useMatchUnitTranslations();

  const { data: visibilityOptions } = useGetMatchDetailsSectionsQuery({
    matchId: matchData.id,
    competitions: matchData.competition.id,
    language: i18n.language,
  });

  return (
    <div className={`${style.grayScale} ${style.whiteBg} pk-m--0 pk-p--0 pk-mb--xl2 pk-pb--xl`}>
      <PkMatchHeader matchData={matchData} {...muTranslations} className="pk-p--m pk-pt--xl" />
      <Sponsors matchData={matchData} isAvailableLinks={false} />
      <div className="pk-mb--s pk-px-md--xl pk-px--m pk-py--l">
        <h3 className={`${Title} pk-text--text-01`}>{t('overview')}</h3>
      </div>
      <PkDivider class="divider--horizontal" direction="horizontal" />
      <HeadToHead matchData={matchData} competitionId={matchData.competition.id} isDesktop isResultsExpanded />
      <FormGuide isDesktop matchData={matchData} isResultsExpanded />
      <LineUpsTab fieldSize={fieldSize} prevLineUpsFieldSize={prevLineUpsFieldsSize} isPDFPrint />
      <MatchHighlights isDesktop competitionId={matchData.competition.id} matchId={matchData.id} isPDFPrint />
      <MatchOfficialsTab isPDFPrint />
      <StatisticsTab isPDFPrint />
      {!visibilityOptions?.disableSquadList && (
        <div className="pr-page-break--before">
          <div className={`${Title} pk-text--text-01 pk-px--xs`}>{t('squad_details')}</div>
          <SquadDetailsTab isPDFPrint />
        </div>
      )}
    </div>
  );
};
